<template>
  <div v-if="location" class="min-h-screen bg-gradient-to-b from-soft-white to-lavender-tint/20">
    <section class="container mx-auto px-6 pt-10 pb-16 max-w-6xl">
      <nav class="text-sm text-dark-gray/60 mb-4 flex flex-wrap gap-2">
        <RouterLink to="/" class="hover:text-light-purple">Home</RouterLink>
        <span>/</span>
        <RouterLink to="/services" class="hover:text-light-purple">Services</RouterLink>
        <span>/</span>
        <span class="text-dark-gray">{{ location.name }}</span>
      </nav>

      <div class="grid lg:grid-cols-[1.2fr_0.8fr] gap-10 items-start">
        <div>
          <p class="text-sm font-semibold text-light-purple uppercase tracking-wide mb-2">
            District coverage · Unique local page
          </p>
          <h1 class="text-3xl md:text-5xl font-poppins font-bold text-dark-gray mb-4 leading-tight">
            Corporate Health Camps & Phlebotomy in
            <span class="text-light-purple"> {{ displayName }}</span>
          </h1>
          <p class="text-lg text-dark-gray/75 mb-6 max-w-2xl">{{ location.blurb }}</p>
          <p v-if="location.twinSlug" class="text-sm text-dark-gray/60 mb-6">
            Also searched as
            <RouterLink
              :to="`/services/${location.twinSlug}`"
              class="text-light-purple font-semibold"
            >{{ location.twinLabel }}</RouterLink>
            — separate URL, same Homvisit coverage region.
          </p>

          <div class="flex flex-wrap gap-3 mb-8">
            <a href="#enquiry" class="btn-neumorphic !py-3 text-light-purple">Request quote</a>
            <RouterLink to="/services/phlebotomy-services-kerala" class="btn-neumorphic !py-3 text-dark-gray text-sm">
              Statewide services
            </RouterLink>
          </div>

          <section class="card-neumorphic !p-6 mb-4">
            <h2 class="text-xl font-semibold text-dark-gray mb-3">Cities & towns we cover</h2>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="c in location.cities"
                :key="c"
                class="text-xs px-3 py-1 rounded-full bg-lavender-tint/50 text-dark-gray/80"
              >{{ c }}</span>
            </div>
          </section>

          <section class="grid md:grid-cols-2 gap-4 mb-4">
            <div class="card-neumorphic !p-5">
              <h2 class="font-semibold text-dark-gray mb-2">Typical response / lead time</h2>
              <p class="text-sm text-dark-gray/70">{{ location.responseNote }}</p>
            </div>
            <div class="card-neumorphic !p-5">
              <h2 class="font-semibold text-dark-gray mb-2">Local camp experience</h2>
              <p class="text-sm text-dark-gray/70">{{ location.localExperience }}</p>
            </div>
          </section>

          <section class="card-neumorphic !p-6 mb-4">
            <h2 class="text-xl font-semibold text-dark-gray mb-2">Diagnostic & hospital ecosystem</h2>
            <p class="text-dark-gray/75 text-sm leading-relaxed">{{ location.ecosystem }}</p>
            <p v-if="location.testimonial" class="mt-3 text-sm italic text-dark-gray/60">
              “{{ location.testimonial }}”
            </p>
          </section>

          <section class="card-neumorphic !p-6 mb-4">
            <h2 class="text-xl font-semibold text-dark-gray mb-3">{{ location.name }} FAQs</h2>
            <div v-for="f in location.faqs" :key="f.q" class="mb-3">
              <h3 class="font-semibold text-dark-gray text-sm">{{ f.q }}</h3>
              <p class="text-sm text-dark-gray/70">{{ f.a }}</p>
            </div>
          </section>

          <section class="card-neumorphic !p-6 mb-4">
            <h2 class="text-xl font-semibold text-dark-gray mb-3">Services buyers search for</h2>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="kw in keywords.slice(0, 10)"
                :key="kw"
                class="text-xs px-3 py-1 rounded-full bg-soft-white border border-light-purple/20"
              >{{ kw }}</span>
            </div>
            <div class="mt-4 flex flex-wrap gap-3 text-sm">
              <RouterLink class="text-light-purple" to="/services/corporate-health-camps">Corporate camps</RouterLink>
              <RouterLink class="text-light-purple" to="/services/mobile-phlebotomy">Mobile phlebotomy</RouterLink>
              <RouterLink class="text-light-purple" to="/services/diagnostic-lab-partner">Lab partner</RouterLink>
            </div>
          </section>

          <p class="text-sm text-dark-gray/60">
            Home blood collection is available as a secondary service —
            <RouterLink to="/services/home-blood-collection" class="text-light-purple">learn more</RouterLink>.
          </p>
        </div>

        <RichCtaCard
          :subject="`District enquiry — ${displayName}`"
          :source-page="`/services/${location.slug}`"
          :subtitle="`Camps & onsite phlebotomy in ${displayName}.`"
        />
      </div>
    </section>
  </div>
  <div v-else class="p-20 text-center">Location not found.</div>
</template>

<script>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import RichCtaCard from '../components/RichCtaCard.vue'
import { getServiceLocation, primaryKeywordsFor } from '../config/locations'
import { SITE_URL } from '../config/contact'
import {
  breadcrumbSchema,
  faqSchema,
  fitDescription,
  fitTitle,
  graphSchema,
  organizationSchema,
  serviceSchema,
  useSeo,
} from '../composables/useSeo'

function districtTitle(name) {
  const long = `${name} Corporate Health Camps & Phlebotomy | Homvisit`
  if (long.length <= 60) return long
  return fitTitle(`${name} Camps & Onsite Phlebotomy | Homvisit`)
}

function districtDescription(loc) {
  const base = loc.blurb.replace(/\s+/g, ' ').trim()
  if (base.length >= 140) return fitDescription(base, 160)
  return fitDescription(
    `${base} Request corporate camps or onsite phlebotomy in ${loc.name}, Kerala with Homvisit. Call 75 9409 9409.`,
    160
  )
}

export default {
  name: 'ServiceLocationPage',
  components: { RichCtaCard },
  setup() {
    const route = useRoute()
    const location = computed(() => getServiceLocation(route.params.slug))
    const displayName = computed(() =>
      location.value ? location.value.aliases.join(' / ') : ''
    )
    const keywords = computed(() =>
      location.value ? primaryKeywordsFor(location.value) : []
    )

    useSeo(
      computed(() => {
        const loc = location.value
        if (!loc) return { title: 'Not found', robots: 'noindex', path: '/services' }
        const path = `/services/${loc.slug}`
        const description = districtDescription(loc)
        return {
          title: districtTitle(loc.name),
          description,
          path,
          keywords: primaryKeywordsFor(loc),
          schema: graphSchema([
            organizationSchema(),
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Services', path: '/services' },
              { name: loc.name, path },
            ]),
            serviceSchema({
              name: `Phlebotomy & corporate camps in ${loc.name}`,
              description,
              area: loc.aliases.join(', '),
              url: `${SITE_URL}${path}`,
            }),
            faqSchema(loc.faqs),
          ]),
        }
      })
    )

    return { location, displayName, keywords }
  },
}
</script>
